import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './AjustarNocautes.css';

/**
 * Página para Admin ajustar nocautes de uma rodada
 * Permite visualizar, deletar e corrigir nocautes
 */

const TIPO_LABEL = {
  normal: '🥊 Normal',
  eliminacao: '💀 Eliminação',
  eliminacao_pos_addon: '💀 Eliminação (pós add-on)',
};

const AjustarNocautes = () => {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodada, setRodada] = useState(null);
  const [nocautes, setNocautes] = useState([]);
  const [jogadores, setJogadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingNocaute, setEditingNocaute] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [id, isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      // Buscar rodada primeiro para validar
      const rodadasRes = await apiClient.get('/rodadas');
      const rodadaId = parseInt(id);
      const rodadaEncontrada = rodadasRes.data.find(r => {
        const rId = typeof r.id === 'string' ? parseInt(r.id) : r.id;
        return rId === rodadaId || r.id === id || String(r.id) === String(id);
      });

      if (!rodadaEncontrada) {
        setError(`Rodada com ID ${id} não encontrada.`);
        setLoading(false);
        return;
      }

      setRodada(rodadaEncontrada);

      // Buscar participações, jogadores e nocautes
      const [participacoesRes, jogadoresRes, nocautesRes] = await Promise.all([
        apiClient.get(`/rodadas/${id}/participacoes-completas`),
        apiClient.get('/jogadores'),
        apiClient.get(`/nocautes/rodada/${id}`),
      ]);

      // Filtrar apenas jogadores que estão participando da rodada
      const participantesIds = (participacoesRes.data || []).map(p => p.jogador_id);
      const jogadoresParticipantes = (jogadoresRes.data || []).filter(j => 
        participantesIds.includes(j.id)
      );

      setJogadores(jogadoresParticipantes);
      setNocautes(nocautesRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados da rodada');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async (nocauteId, nocauteadorNome, nocauteadoNome) => {
    if (!window.confirm(`Deseja realmente deletar o nocaute de ${nocauteadorNome} sobre ${nocauteadoNome}?`)) {
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      await apiClient.delete(`/nocautes/${nocauteId}`);
      setSuccess('Nocaute deletado com sucesso!');
      await carregarDados();
    } catch (err) {
      console.error('Erro ao deletar nocaute:', err);
      setError(err.response?.data?.error || 'Erro ao deletar nocaute');
    } finally {
      setSalvando(false);
    }
  };

  const handleEditar = (nocaute) => {
    setEditingNocaute({
      ...nocaute,
      nocauteador_id: nocaute.nocauteador?.id || nocaute.nocauteador_id,
      nocauteado_id: nocaute.nocauteado?.id || nocaute.nocauteado_id,
    });
    setError('');
    setSuccess('');
  };

  const handleCancelarEdicao = () => {
    setEditingNocaute(null);
    setError('');
    setSuccess('');
  };

  const handleSalvarEdicao = async () => {
    if (!editingNocaute) return;

    if (!editingNocaute.nocauteador_id || !editingNocaute.nocauteado_id) {
      setError('Selecione o nocauteador e o nocauteado');
      return;
    }

    if (editingNocaute.nocauteador_id === editingNocaute.nocauteado_id) {
      setError('O nocauteador não pode ser o mesmo que o nocauteado');
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      await apiClient.put(`/nocautes/${editingNocaute.id}`, {
        nocauteador_id: editingNocaute.nocauteador_id,
        nocauteado_id: editingNocaute.nocauteado_id,
      });

      setSuccess('Nocaute atualizado com sucesso!');
      setEditingNocaute(null);
      await carregarDados();
    } catch (err) {
      console.error('Erro ao atualizar nocaute:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar nocaute');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodada) {
    return (
      <div className="container">
        <div className="error">
          <h2>Rodada não encontrada</h2>
          <p>ID da rodada: {id}</p>
          {error && <p style={{ color: 'red', marginTop: '10px' }}>Erro: {error}</p>}
        </div>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-primary" style={{ marginTop: '20px' }}>
          Voltar para Menu Admin
        </button>
      </div>
    );
  }

  const rodadaAberta = rodada.status === 'aberta';

  return (
    <div className="container ajustar-nocautes-container">
      <div className="page-header">
        <h1>🥊 Ajustar Nocautes</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {/* Informações da Rodada */}
      <div className="rodada-info-card">
        <h2>Informações da Rodada</h2>
        <div className="info-grid">
          <div>
            <strong>Data:</strong> {new Date(rodada.data).toLocaleDateString('pt-BR')}
          </div>
          <div>
            <strong>Status:</strong> 
            <span className={`status-badge ${rodada.status}`}>
              {rodada.status === 'aberta' ? '🟢 Aberta' : '🔴 Fechada'}
            </span>
          </div>
          <div>
            <strong>Total de Nocautes:</strong> {nocautes.length}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {!rodadaAberta && (
        <div className="info-text" style={{ background: '#fff3cd', padding: '15px', borderRadius: '5px', marginBottom: '20px' }}>
          ⚠️ Esta rodada está fechada. Apenas visualização permitida.
        </div>
      )}

      {/* Tabela de Nocautes */}
      <div className="nocautes-section">
        <h2>Nocautes Registrados</h2>

        {nocautes.length === 0 ? (
          <div className="no-data">
            <p>Nenhum nocaute registrado nesta rodada ainda.</p>
          </div>
        ) : (
          <table className="nocautes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nocauteador</th>
                <th>Nocauteado</th>
                <th>Tipo</th>
                <th>Data/Hora</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {nocautes.map((nocaute, index) => {
                const isEditing = editingNocaute?.id === nocaute.id;

                return (
                  <tr key={nocaute.id} className={isEditing ? 'editing-row' : ''}>
                    <td>{index + 1}</td>
                    <td>
                      {isEditing ? (
                        <select
                          value={editingNocaute.nocauteador_id}
                          onChange={(e) => setEditingNocaute({
                            ...editingNocaute,
                            nocauteador_id: e.target.value,
                          })}
                          className="form-control"
                        >
                          <option value="">Selecione...</option>
                          {jogadores.map(j => (
                            <option key={j.id} value={j.id}>
                              {j.nome}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <strong>{nocaute.nocauteador?.nome || 'Jogador não encontrado'}</strong>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <select
                          value={editingNocaute.nocauteado_id}
                          onChange={(e) => setEditingNocaute({
                            ...editingNocaute,
                            nocauteado_id: e.target.value,
                          })}
                          className="form-control"
                        >
                          <option value="">Selecione...</option>
                          {jogadores.map(j => (
                            <option key={j.id} value={j.id}>
                              {j.nome}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <strong>{nocaute.nocauteado?.nome || 'Jogador não encontrado'}</strong>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge tipo-${(nocaute.tipo || 'normal').replace(/_/g, '-')}`}>
                        {TIPO_LABEL[nocaute.tipo] || TIPO_LABEL.normal}
                      </span>
                    </td>
                    <td>
                      {nocaute.createdAt
                        ? new Date(nocaute.createdAt).toLocaleString('pt-BR')
                        : '-'
                      }
                    </td>
                    <td>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <button
                            onClick={handleSalvarEdicao}
                            disabled={salvando}
                            className="btn btn-sm btn-success"
                            style={{ padding: '3px 8px', fontSize: '11px' }}
                          >
                            {salvando ? 'Salvando...' : '✓ Salvar'}
                          </button>
                          <button
                            onClick={handleCancelarEdicao}
                            className="btn btn-sm btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11px' }}
                          >
                            ✕ Cancelar
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <button
                            onClick={() => handleEditar(nocaute)}
                            disabled={!rodadaAberta}
                            className="btn btn-sm btn-primary"
                            style={{ padding: '3px 8px', fontSize: '11px' }}
                            title={rodadaAberta ? 'Editar nocaute' : 'Apenas rodadas abertas podem ser editadas'}
                          >
                            ✏️ Editar
                          </button>
                          {rodadaAberta && (
                            <button
                              onClick={() => handleDeletar(
                                nocaute.id,
                                nocaute.nocauteador?.nome || 'Jogador',
                                nocaute.nocauteado?.nome || 'Jogador'
                              )}
                              disabled={salvando}
                              className="btn btn-sm btn-danger"
                              style={{ padding: '3px 8px', fontSize: '11px' }}
                              title="Deletar nocaute"
                            >
                              🗑️ Deletar
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AjustarNocautes;
